import { createAuth0 } from '@auth0/auth0-vue';

const url = new URL(window.location.href);
const subdomain = url.host.split('.')[0];

// Hosts that are not organization-scoped: local development and the bare
// per-stage hosts. Every other host is <organization>.<stage host>.
const SHARED_HOSTS = ['dev', 'staging', 'arcane', 'localhost:3000'];

// ARP-3441: the Anthill Cloud tenant is the token issuer; users still authenticate
// against the Arcane tenant through the `arcane` enterprise OIDC connection
// (CLOUD-612/613), which the organization selects — the connection is not named here.
// Organizations are matched by name across the two tenants and the Cloud tenant allows
// organization names in the Authentication API, so the subdomain goes through as-is.
// ARP-3480: the shared hosts carry no organization in their subdomain and no longer fall
// back to a configured one. They send no organization at all, so the Cloud tenant resolves
// it on its own pre-login prompt (CLOUD-769) — a supplied organization skips that prompt.
const organization = SHARED_HOSTS.includes(subdomain) ? null : subdomain;

// ARP-3489: `login_hint` only means "organization" on the Anthill Cloud tenant, where
// the `arcane` connection aliases it. Everywhere else it keeps its standard Auth0
// meaning — a username/UPN prefill passed on to the upstream IdP — and a bare subdomain
// is not a valid one: Azure AD reads it as a UPN and errors rather than prefilling.
//
// Staging, production and mca are all still on the Arcane tenant, and stay there until
// there is a Cloud environment for them (ARP-3486). So gate the hint on the tenant that
// is actually configured rather than on having resolved an organization.
//
// The Cloud tenants are reached through their per-environment custom domain, which is
// deliberate (ARP-3463: the issuer is whichever hostname minted the token). If a Cloud
// tenant is ever pointed at its canonical `<tenant>.eu.auth0.com` host instead, this
// check stops matching and `organization` silently stops reaching the upstream IdP —
// change this constant in the same commit.
const CLOUD_TENANT_DOMAIN_SUFFIX = '.cloud.anthill.technology';
const isCloudTenant = (import.meta.env.VUE_APP_AUTH0_DOMAIN ?? '').endsWith(CLOUD_TENANT_DOMAIN_SUFFIX);

const auth0Client = createAuth0({
  domain: import.meta.env.VUE_APP_AUTH0_DOMAIN,
  clientId: import.meta.env.VUE_APP_AUTH0_CLIENT_ID,
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  cacheLocation: 'localstorage',
  responseType: 'code',
  grantType: 'authorization_code',
  legacySameSiteCookie: false,
  cookieDomain: '.anthill.technology',
  authorizationParams: {
    redirect_uri: window.location.origin + '/callback',
    audience: import.meta.env.VUE_APP_AUTH0_AUDIENCE,
    scope: 'openid, profile, email, offline_access',
    grant_type: 'authorization_code',
    response_type: 'code',
    // `organization` goes to every tenant, as it did before ARP-3441. With none
    // resolved we send nothing, which is the pre-ARP-3441 behaviour for shared hosts.

    // Auth0 cannot forward `organization` to an upstream IdP; the `arcane` connection
    // aliases login_hint to it instead. See infrastructure/docs/sso-arcane.md in the
    // anthill-cloud repository. That aliasing exists only on the Cloud tenant, hence
    // the guard — see CLOUD_TENANT_DOMAIN_SUFFIX above.
    ...(organization ? { organization, login_hint: organization } : {}),
  },
});

export default auth0Client;
